// src/components/profile/ShareMenu.jsx
// ============================================================
// CLIENT ID: Accepts clientId as STRING prop, no hardcoded default
// FIX: Changed from numeric 1 to null - parent MUST pass string clientId
// ============================================================
import React, { useState } from 'react';
import { Share2, Link, FileText, FileJson, ChevronDown, Check, X } from 'lucide-react';

export default function ShareMenu({
  useCaseId,
  // ============================================================
  // FIX: Changed from numeric 1 to null
  // Parent component MUST pass clientId as STRING from AuthContext
  // ============================================================
  clientId = null,
  lens = '70_30',
  displayMode = 'raw'
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

  const getPermalink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/use-cases/${useCaseId}?client=${clientId}&lens=${lens}&display=${displayMode}`;
  };

  const handleCopyLink = async () => {
    if (!clientId) {
      console.warn('ShareMenu: clientId is required for copy link');
      return;
    }
    try {
      await navigator.clipboard.writeText(getPermalink());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleDownloadPDF = async () => {
    if (!clientId) {
      console.warn('ShareMenu: clientId is required for PDF download');
      return;
    }
    setDownloading(true);
    try {
      const url = `${API_BASE}/api/v2/export/profile/pdf?client_id=${clientId}&use_case_id=${useCaseId}&lens=${lens}&display=${displayMode}`;
      const response = await fetch(url, { credentials: 'include' });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `use-case-${useCaseId}-profile.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
      } else {
        console.error('PDF download failed, falling back to client-side');
        // Fallback to client-side PDF generation if needed
      }
    } catch (error) {
      console.error('PDF download error:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadMarkdown = async () => {
    if (!clientId) {
      console.warn('ShareMenu: clientId is required for Markdown download');
      return;
    }
    setDownloading(true);
    try {
      const url = `${API_BASE}/api/v2/export/profile/markdown?client_id=${clientId}&use_case_id=${useCaseId}&lens=${lens}&display=${displayMode}`;
      const response = await fetch(url, { credentials: 'include' });

      if (response.ok) {
        const text = await response.text();
        const blob = new Blob([text], { type: 'text/markdown' });
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `use-case-${useCaseId}-profile.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error('Markdown download error:', error);
    } finally {
      setDownloading(false);
    }
  };

  // Don't render if clientId is missing
  if (!clientId) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Share2 className="w-4 h-4" />
        <span className="text-sm font-medium">Share</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="py-1">
            <button
              onClick={handleCopyLink}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Link className="w-4 h-4" />}
              <span>{copied ? 'Link Copied!' : 'Copy Link'}</span>
            </button>

            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <FileText className="w-4 h-4" />
              <span>Download PDF</span>
            </button>

            <button
              onClick={handleDownloadMarkdown}
              disabled={downloading}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <FileText className="w-4 h-4" />
              <span>Download Markdown</span>
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}